import { Box, Button, Caption, Card, Heading, LogoLoader, TextBlock, TextInput } from "@bcc-ui/ui-components";
import * as React from "react";
import { useTranslation } from "react-i18next";

import EmptyStateIcon from "components/icons/EmptyState";
import ApplicationLayout from "components/layout/ApplicationLayout";
import { PassportResultCard } from "components/reports/passports/card/PassportResultCard";
import style from "components/reports/passports/passports-view.scss";
import { REPORTS_ROUTE } from "components/router/Routes";
import { FetchPassportsResponse, passportService } from "services/report/passports/PassportService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { truncateWithEllipsis } from "utils/format";

import testIds from "testIds.json";

export const PassportsView = () => {
    const { t } = useTranslation();
    const [searchValue, setSearchValue] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [requestFailureMessage, setRequestFailureMessage] = React.useState("");
    const [result, setResult] = React.useState<FetchPassportsResponse | null>(null);
    const [searchedQuery, setSearchedQuery] = React.useState("");
    const abortControllerRef = React.useRef<AbortController | null>(null);

    React.useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    const handleSearch = () => {
        if (!searchValue.trim()) {
            return;
        }
        usageStatisticsService.sendEvent({
            category: Category.DEVICE_PASSPORT,
            action: Action.SEARCH_DEVICE_PASSPORT,
        });
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        setLoading(true);
        setRequestFailureMessage("");
        setResult(null);
        setSearchedQuery(searchValue.trim());

        passportService
            .fetchPassports(searchValue.trim(), abortController)
            .then((response) => {
                if (abortController.signal.aborted || abortControllerRef.current !== abortController) {
                    return;
                }
                setResult(response);
            })
            .catch(() => {
                if (abortController.signal.aborted || abortControllerRef.current !== abortController) {
                    return;
                }
                setRequestFailureMessage(t("DevicePassport.requestFailed"));
            })
            .finally(() => {
                if (abortControllerRef.current === abortController) {
                    setLoading(false);
                }
            });
    };

    const view = (
        <Box padding="Sm" columnLayout className={style.container}>
            <Card className={style.card}>
                <Heading tag="div" variant="h2" disableTopSpacing disableBottomSpacing className={style.title}>
                    {t("DevicePassport.title")}
                </Heading>
                <TextBlock disableBottomSpacing alignCenter>
                    {t("DevicePassport.subtitle")}
                </TextBlock>
                <Box verticalAlignment={"Center"} horizontalAlignment={"Center"} gap={"Xs"} className={style.searchRow}>
                    <TextInput
                        name="passport-search"
                        className={style.searchInput}
                        dataType="PASSPORT_SEARCH"
                        placeholder={t("DevicePassport.inputPlaceholder")}
                        value={searchValue}
                        onChange={(e) => {
                            setSearchValue(e.target.value);
                            if (!e.target.value.trim()) {
                                setResult(null);
                                setRequestFailureMessage("");
                            }
                        }}
                        onKeyDown={handleKeyDown}
                        disabled={loading}
                        data-testid="workArea-passports-searchInput"
                    />
                    <Button
                        variant="Primary"
                        testId={testIds.workArea.passports.searchButton}
                        onClick={handleSearch}
                        disabled={loading || !searchValue.trim()}
                        className={style.searchButton}
                    >
                        {t("DevicePassport.searchButton")}
                    </Button>
                </Box>
                <Caption className={style.footnote}>{t("DevicePassport.footnote")}</Caption>
            </Card>
            {loading && (
                <div className={style.resultsArea}>
                    <LogoLoader testId={testIds.common.inProgressElement} />
                </div>
            )}
            {requestFailureMessage && (
                <div className={style.resultsArea}>
                    <p className={style.errorMessage} data-testid={testIds.common.errorMessage.itself}>
                        {requestFailureMessage}
                    </p>
                </div>
            )}
            {result && !loading && result.reports.length === 0 && (
                <div className={style.resultsArea}>
                    <EmptyStateIcon />
                    <p className={style.noResults}>
                        {t("Common.searchEmptyStateMessage", { searchQuery: truncateWithEllipsis(searchedQuery) })}
                    </p>
                </div>
            )}
            {result &&
                !loading &&
                result.reports.map((report, index) => (
                    <PassportResultCard key={index} report={report} searchValue={searchedQuery} />
                ))}
        </Box>
    );

    return <ApplicationLayout viewTitle={t("Routes.devicePassport")} view={view} mainRoute={REPORTS_ROUTE} />;
};
